import { Check, Minus, ShieldCheck } from 'lucide-react';
import { Card } from '../../shared/ui/Card.js';
import { useRolePermissions } from '../../shared/api/queries.js';

/** Plain words for each permission of technical §8.4, in the order of the rights matrix (functional §9.3). */
const PERMISSION_LABELS: Readonly<Record<string, { label: string; hint: string }>> = {
  'project:read': { label: 'Consulter ses projets', hint: 'Voir les projets de son périmètre.' },
  'analytics:read': {
    label: 'Lire les indicateurs',
    hint: 'Tableau de bord, filtres, entonnoir, avant / après.',
  },
  'analytics:export': {
    label: 'Extraire les données',
    hint: 'Accès plateforme : données agrégées de ses projets seulement.',
  },
  'journey:manage': {
    label: 'Gérer les parcours',
    hint: 'Renommer, regrouper, suivre ; jamais créer à la main.',
  },
  'recommendation:decide': { label: 'Décider des recommandations', hint: 'Accepter, ajuster ou écarter.' },
  'guidance:compose': { label: 'Ajuster les guidances', hint: 'Modifier les contenus proposés.' },
  'guidance:publish': { label: 'Publier les guidances', hint: 'Valider, publier, désactiver.' },
  'analytics:read-raw': {
    label: 'Consulter les données brutes',
    hint: 'Chaque consultation est journalisée.',
  },
  'analysis:read-chain': {
    label: "Lire la chaîne d'analyse",
    hint: 'Comprendre comment les scores sont calculés.',
  },
  'analysis:manage-settings': { label: "Régler l'analyse", hint: 'Pondérations et volume minimal.' },
  'project:manage': { label: 'Gérer les projets', hint: 'Créer, modifier, supprimer une plateforme.' },
  'script:administer': {
    label: 'Administrer le script',
    hint: 'Interrupteur, versions, balise d’intégration.',
  },
  'account:manage': { label: 'Gérer les comptes', hint: 'Créer, suspendre, retirer ; profil et périmètre.' },
};

const ORDER = Object.keys(PERMISSION_LABELS);

/**
 * The two profiles and what each allows, read from the API (functional §9.3):
 * fixed by the contract, shown so anyone creating an account knows what it grants.
 */
export function RolePermissionsCard() {
  const { data } = useRolePermissions();
  if (!data) return null;
  const rows = [...data.permissions].sort((a, b) => ORDER.indexOf(a.key) - ORDER.indexOf(b.key));

  return (
    <Card title="Droits par profil" icon={<ShieldCheck size={15} />} className="accounts__settings">
      <p className="accounts__lead">
        Deux profils, fixés par le contrat. Un accès plateforme n'agit que sur les projets de son périmètre ;
        il ne voit jamais les données d'un autre projet.
      </p>
      <div className="table__scroll">
        <table className="table rights">
          <thead>
            <tr>
              <th>Droit</th>
              <th className="rights__role">Accès plateforme</th>
              <th className="rights__role">Accès central</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const copy = PERMISSION_LABELS[row.key] ?? { label: row.key, hint: '' };
              return (
                <tr key={row.key}>
                  <td>
                    <span className="rights__label">{copy.label}</span>
                    {copy.hint && <span className="rights__hint">{copy.hint}</span>}
                  </td>
                  {(['platform_access', 'central_access'] as const).map((role) => (
                    <td key={role} className="rights__role">
                      {row.roles.includes(role) ? (
                        <Check size={16} className="rights__yes" aria-label="Oui" />
                      ) : (
                        <Minus size={16} className="rights__no" aria-label="Non" />
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
